import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Drawer,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/ui/DataTable';
import { LoadingPage } from '../../components/ui/LoadingSpinner';
import { formatDate, formatAnalyticsCount } from '../../lib/utils';
import apiClient from '../../lib/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import GooglePlacesAutocomplete from '../../components/GooglePlacesAutocomplete';
import { normalizeListPagination } from '../../lib/normalizeListPagination';

const ObituariesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentAccountType, isLoading: authLoading } = useAuth();
  
  const [obituaries, setObituaries] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [loading, setLoading] = useState(true);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [adminBranches, setAdminBranches] = useState([]);

  // Load obituaries data — deps are primitives only so /me refreshing `user` doesn't change callback identity
  const loadObituaries = useCallback(async (searchParams) => {
    try {
      setLoading(true);

      const rowsPerPage = searchParams?.rowsPerPage ?? 10;
      const params = {
        page: searchParams?.page ?? 1,
        limit: rowsPerPage,
      };

      if (searchParams?.searchTerm) {
        params.search = searchParams.searchTerm;
      }

      if (user?.type !== 'admin') {
        if (currentAccountType === 'funeral_director' && user?.id) {
          params.funeralDirectorId = user.id;
        } else if (currentAccountType === 'staff' && user?.companyId) {
          if (user.allBranches) {
            params.companyId = user.companyId;
          } else if (user.branchIds && user.branchIds.length > 0) {
            params.branchIds = user.branchIds.join(',');
          }
        }
      }

      const response = await apiClient.getObituaries(params);

      if (response.data) {
        setObituaries(response.data);
        setPagination(normalizeListPagination(response, { limit: rowsPerPage }));
      } else {
        setObituaries([]);
        setPagination({
          page: 1,
          limit: rowsPerPage,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (error) {
      console.error('Error loading obituaries:', error);
      toast.error('Failed to load obituaries');
      setObituaries([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
    }
  }, [
    currentAccountType,
    user?.id,
    user?.type,
    user?.companyId,
    user?.allBranches,
    (user?.branchIds ?? []).join(','),
  ]);

  // Load branches for staff users - load all branches they have access to
  useEffect(() => {
    const loadBranches = async () => {
      if (currentAccountType === 'staff') {
        // If admin is emulating staff, show all branches for testing
        // Otherwise, show branches based on staff user's access
        if (user?.type === 'admin') {
          // Admin emulating staff - load all branches for testing
          try {
            const resp = await apiClient.getBranches({ page: 1, limit: 100 });
            setBranches(resp.data || []);
          } catch (err) {
            console.error('Failed to load branches', err);
            setBranches([]);
          }
        } else if (user?.companyId) {
          // Actual staff user - load branches based on their access
          try {
            const resp = await apiClient.getBranches({ companyId: user.companyId, page: 1, limit: 100 });
            // If allBranches is true, show all branches. Otherwise, filter to only show branches the user is associated with
            const userBranches = user.allBranches 
              ? (resp.data || [])
              : (resp.data || []).filter(branch => 
                  user.branchIds && user.branchIds.includes(branch.id)
                );
            setBranches(userBranches);
          } catch (err) {
            console.error('Failed to load branches', err);
            setBranches([]);
          }
        } else {
          setBranches([]);
        }
      } else if (currentAccountType === 'funeral_director' && user?.companyId) {
        // Funeral directors can also create obituaries, so load their company's branches
        try {
          const resp = await apiClient.getBranches({ companyId: user.companyId, page: 1, limit: 100 });
          setBranches(resp.data || []);
        } catch (err) {
          console.error('Failed to load branches', err);
          setBranches([]);
        }
      } else if (currentAccountType === 'admin') {
        setBranches([]);
        setAdminBranches([]);
      } else {
        setBranches([]);
      }
    };
    if (!authLoading && user) {
      loadBranches();
    }
  }, [authLoading, user, currentAccountType]);

  // Load funeral companies for admin when create drawer opens
  useEffect(() => {
    const loadCompanies = async () => {
      if (currentAccountType === 'admin' && createDrawerOpen) {
        try {
          const resp = await apiClient.getCompanies({ companyType: 'funeral', page: 1, limit: 100 });
          setCompanies(resp.data || []);
        } catch (err) {
          console.error('Failed to load companies', err);
          toast.error('Failed to load funeral companies');
          setCompanies([]);
        }
      }
    };
    loadCompanies();
  }, [currentAccountType, createDrawerOpen]);

  // Load branches for selected company when admin is creating obituary
  useEffect(() => {
    const loadAdminBranches = async () => {
      if (currentAccountType === 'admin' && formData.companyId) {
        try {
          const resp = await apiClient.getBranches({ companyId: formData.companyId, page: 1, limit: 100 });
          setAdminBranches(resp.data || []);
        } catch (err) {
          console.error('Failed to load branches for company', err);
          setAdminBranches([]);
        }
      } else {
        setAdminBranches([]);
      }
    };
    loadAdminBranches();
  }, [currentAccountType, formData.companyId]);

  // Initial load + refetch when account context changes — use user?.id, not user, so a new object
  // reference from /me or context doesn't fire after every pagination request and wipe page 2 back to page 1 data.
  useEffect(() => {
    if (!authLoading && user) {
      loadObituaries({ page: 1 });
    }
  }, [authLoading, currentAccountType, user?.id, loadObituaries]);

  const columns = [
    {
      id: 'deceasedName',
      label: 'Deceased Name',
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {value || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'dateOfDeath',
      label: 'Date of Death',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDate(value) : 'Unknown'}
        </Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDate(value) : 'Unknown'}
        </Typography>
      ),
    },
    {
      id: 'viewed',
      label: 'Views',
      sortable: true,
      render: (_, row) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAnalyticsCount(row.viewed)}
        </Typography>
      ),
    },
    {
      id: 'storeVisits',
      label: 'Store',
      sortable: true,
      render: (_, row) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAnalyticsCount(row.storeVisits)}
        </Typography>
      ),
    },
    {
      id: 'cardsSold',
      label: 'Cards',
      sortable: true,
      render: (_, row) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAnalyticsCount(row.cardsSold)}
        </Typography>
      ),
    },
  ];

  const filters = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by name or title...'
    },
  ];

  // Handle obituary actions
  const handleRowSelect = (obituary) => {
    navigate(`/obituaries/${obituary.id}`);
  };

  const handleSearch = (searchParams) => {
    console.log('Search params:', searchParams);
    loadObituaries(searchParams);
  };

  const handleAddObituary = () => {
    setFormData({
      deceasedName: '',
      dateOfDeath: '',
      branchId: '',
      companyId: currentAccountType === 'admin' ? '' : undefined,
    });
    setCreateDrawerOpen(true);
  };

  const handleSave = async () => {
    // Validate branch selection for staff and funeral directors
    if ((currentAccountType === 'staff' || currentAccountType === 'funeral_director') && branches.length > 0 && !formData.branchId) {
      toast.error('Please select a branch');
      return;
    }
    // Admin must select a funeral company and branch
    if (currentAccountType === 'admin') {
      if (!formData.companyId) {
        toast.error('Please select a funeral company');
        return;
      }
      if (!formData.branchId) {
        toast.error('Please select a branch');
        return;
      }
    }

    try {
      setSaving(true);
      const response = await apiClient.createObituary(formData);
      toast.success('Obituary created successfully');
      setCreateDrawerOpen(false);
      // Navigate to the detail page
      if (response.success && response.data && response.data.id) {
        navigate(`/obituaries/${response.data.id}`);
      } else {
        loadObituaries();
      }
    } catch (error) {
      console.error('Error creating obituary:', error);
      toast.error('Failed to create obituary');
    } finally {
      setSaving(false);
    }
  };

  // Remove the full-page loading state that causes unmounting
  // Instead, show loading state within the DataTable component

  return (
    <DashboardLayout title="OBITUARY MANAGEMENT">
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Obituary Management
      </Typography>

      {/* Stats Cards */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2
        }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4a8f 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DescriptionIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Obituaries
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {pagination.total}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{
            background: 'linear-gradient(135deg, #d885e8 0%, #e04a5f 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Published
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {obituaries.filter(o => o.status === 'published').length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{
            background: 'linear-gradient(135deg, #4a9ee8 0%, #00d8e0 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EditIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Draft
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {obituaries.filter(o => o.status === 'draft' || !o.status).length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{
            background: 'linear-gradient(135deg, #3dd06f 0%, #32e0c0 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  This Month
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {obituaries.filter(o => {
                  const created = new Date(o.createdAt);
                  const now = new Date();
                  return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Obituaries Table */}
      <Box sx={{ mt: 4 }}>
        <DataTable
          data={obituaries}
          title="Obituaries"
          onAdd={handleAddObituary}
          addButtonText="Create Obituary"
          columns={columns}
          filters={filters}
          onSelect={handleRowSelect}
          onSearch={handleSearch}
          totalPages={pagination.totalPages}
          currentPage={pagination.page}
          loading={loading}
          onRowClick={(row) => navigate(`/obituaries/${row.id}`)}
        />
      </Box>

      {/* Create Drawer */}
      <Drawer anchor="right" open={createDrawerOpen} onClose={() => setCreateDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3, backgroundColor: 'background.paper' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Create Obituary</Typography>
          <IconButton onClick={() => setCreateDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Admin: select funeral company and optional branch */}
          {currentAccountType === 'admin' && (
            <>
              <Alert severity="info">
                Select the funeral company and branch this obituary belongs to.
              </Alert>
              <FormControl fullWidth required>
                <InputLabel>Funeral Company</InputLabel>
                <Select
                  value={formData.companyId || ''}
                  label="Funeral Company"
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value, branchId: '' })}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.companyName || 'Unnamed Company'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.companyId && (
                <FormControl fullWidth required>
                  <InputLabel>Branch</InputLabel>
                  <Select
                    value={formData.branchId || ''}
                    label="Branch"
                    onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                  >
                    {adminBranches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.branchName || 'Unnamed Branch'}
                      </MenuItem>
                    ))}
                  </Select>
                  {adminBranches.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      This company has no branches. Add a branch before creating an obituary.
                    </Alert>
                  )}
                </FormControl>
              )}
            </>
          )}
          {/* Branch selector - show for staff and funeral directors when not in branch context */}
          {(currentAccountType === 'staff' || currentAccountType === 'funeral_director') && branches.length > 0 && (
            <>
              {currentAccountType === 'staff' && user?.allBranches ? (
                <Alert severity="info">
                  You have access to all branches. Please select a branch for this obituary.
                </Alert>
              ) : null}
              <FormControl fullWidth required>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={formData.branchId || ''}
                  label="Branch"
                  onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.branchName || 'Unnamed Branch'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <TextField fullWidth label="Deceased Name" value={formData.deceasedName} onChange={(e) => setFormData({ ...formData, deceasedName: e.target.value })} required />
          <TextField fullWidth label="Date of Death" type="date" value={formData.dateOfDeath} onChange={(e) => setFormData({ ...formData, dateOfDeath: e.target.value })} InputLabelProps={{ shrink: true }} />
        </Box>

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => setCreateDrawerOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>{saving ? 'Creating...' : 'Create'}</Button>
        </Box>
      </Drawer>
    </DashboardLayout>
  );
};

export default ObituariesPage;
